import Papa from "papaparse";
import { pluralize } from "../../../utils/text";
import {
  MAX_MENTIONS_LIMIT,
  MAX_MENTION_ID,
  MENTION_CSV_HEADERS,
} from "./constants";

export interface MentionCsvParseResult {
  /** Unique, well-formed mention ids, in file order. */
  ids: number[];
  /** Unique id cells that are not a valid mention id (not a whole number, too large). */
  invalidValues: string[];
  error: string | null;
}

export interface MentionMergeResult {
  /** The selection after adding the uploaded ids. */
  ids: number[];
  /** Uploaded ids that were not already selected. */
  newCount: number;
  /** How many of those fit under MAX_MENTIONS_LIMIT. */
  addedCount: number;
}

const failure = (error: string): MentionCsvParseResult => ({
  ids: [],
  invalidValues: [],
  error,
});

/** "1 mention", "2,500 mentions" */
export const formatMentionCount = (count: number): string =>
  `${count.toLocaleString()} ${pluralize("mention", count)}`;

const collectMentionIds = (cells: (string | undefined)[]) => {
  const ids = new Set<number>();
  const invalidValues = new Set<string>();
  for (const cell of cells) {
    const value = cell?.trim();
    if (!value) continue;
    // Whole numbers only; "123.0" is what spreadsheets and pandas write for an
    // integer column that has gaps.
    if (/^\d+(\.0+)?$/.test(value) && Number(value) <= MAX_MENTION_ID) {
      ids.add(Number(value));
    } else {
      invalidValues.add(value);
    }
  }
  return { ids: [...ids], invalidValues: [...invalidValues] };
};

/**
 * Parses a CSV file with a `mention_id` column (and optionally `mention_name`,
 * which is ignored: names come from the taxonomy).
 */
export const parseMentionCsv = (file: File): Promise<MentionCsvParseResult> =>
  new Promise((resolve) => {
    Papa.parse<Record<string, string | undefined>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: ({ data, meta, errors }) => {
        if (!meta.fields?.includes("mention_id")) {
          resolve(failure("Missing required column: mention_id"));
          return;
        }

        // Unquoted commas or a stray quote shift cells into the wrong column, so
        // ids would be dropped or misread under a success message.
        if (
          errors.some(
            (error) => error.type === "Quotes" || error.code === "TooManyFields"
          )
        ) {
          resolve(
            failure(
              "The CSV is malformed: a row has an unclosed quote or more cells than the header. Put values that contain commas in double quotes."
            )
          );
          return;
        }

        const { ids, invalidValues } = collectMentionIds(
          data.map((row) => row.mention_id)
        );
        if (ids.length === 0 && invalidValues.length === 0) {
          resolve(failure("No mention IDs found in CSV"));
          return;
        }
        if (ids.length > MAX_MENTIONS_LIMIT) {
          resolve(
            failure(
              `File exceeds limit: ${formatMentionCount(ids.length)} (max: ${MAX_MENTIONS_LIMIT.toLocaleString()})`
            )
          );
          return;
        }

        resolve({ ids, invalidValues, error: null });
      },
      error: (error) =>
        resolve(failure(`Failed to parse CSV: ${error.message}`)),
    });
  });

/** Adds the uploaded ids to the selection, never replacing it and never exceeding the cap. */
export const mergeMentionIds = (
  selectedIds: number[],
  uploadedIds: number[]
): MentionMergeResult => {
  const alreadySelected = new Set(selectedIds);
  const newIds = uploadedIds.filter((id) => !alreadySelected.has(id));
  const slots = Math.max(0, MAX_MENTIONS_LIMIT - selectedIds.length);
  const addedIds = newIds.slice(0, slots);

  return {
    ids: [...selectedIds, ...addedIds],
    newCount: newIds.length,
    addedCount: addedIds.length,
  };
};

export const buildMentionsCsv = (
  mentions: { id: number; name: string }[]
): string =>
  Papa.unparse({
    fields: MENTION_CSV_HEADERS,
    data: mentions.map(({ id, name }) => ({
      mention_name: name,
      mention_id: id,
    })),
  });

export const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
